import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ActionButton } from 'app/components/Button/ActionButton';
import { FormInput } from 'app/components/Input/FormInput';
import { FormSelect } from 'app/components/Select/FormSelect';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { useQueryRequest } from 'app/components/Table/core/QueryRequestProvider';
import { useQueryResponse } from 'app/components/Table/core/QueryResponseProvider';
import { emptyListViewProvider } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { useAuth } from 'app/modules/auth';
import { useChangeParams } from 'app/modules/query-state/hooks';
import { DISPLAY_SENSOR_TYPE } from 'app/modules/system-settings/devices/core/constant';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { getResponseError } from 'app/helpers/errors';
import { useBoolean, useNumber } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';

import { SensorType } from '../core/enum';
import { createSensor, updateSensor } from '../core/requests';
import { Device, Sensor, SensorRequest } from '../core/type';
import { useFormik } from 'formik';
import { useOrganizationStore } from 'theme/layout/components/aside/store';
import * as Yup from 'yup';

type Props = {
  sensor: Sensor;
  titleNotification: string;
  wards: Ward[];
  originIsView?: boolean;
  onShowCodeWhenAddDevice?: (newDevice: Device) => void;
};

const FormDeviceSensorCard: FC<Props> = ({
  sensor,
  titleNotification,
  wards,
  originIsView = false,
  onShowCodeWhenAddDevice,
}) => {
  const { itemForUpdate, setItemForUpdate } = useListView();
  const { refetch } = useQueryResponse();
  const { state } = useQueryRequest();
  const { onChangeParams } = useChangeParams();

  const { currentUser, setCurrentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const organization = useOrganizationStore((s) => s.state.organization);

  const types = [
    {
      title: 'Computer vision expiry',
      value: SensorType.SENSOR_CV_EXPIRY,
    },
    {
      title: 'Computer vision',
      value: SensorType.SENSOR_CV,
    },
    {
      title: 'Weight',
      value: SensorType.SENSOR_WEIGHT,
    },
    {
      title: 'Cath-Rack',
      value: SensorType.SENSOR_HANGER,
    },
  ];

  const [formRequest] = useState<{
    code: string;
    piId: string;
    cameraId: string;
    viewId: string;
    hangerId: string;
    type: SensorType;
  }>({
    code: sensor ? sensor.code : '',
    piId: sensor ? sensor.piId : '',
    cameraId: sensor ? sensor.cameraId : '',
    viewId: sensor ? sensor.viewId : '',
    hangerId: sensor ? sensor.hangerId || '' : '',
    type: sensor ? DISPLAY_SENSOR_TYPE[sensor.skuId] : SensorType.SENSOR_CV_EXPIRY,
  });
  const wardId = useNumber(0);
  const isView = useBoolean(originIsView);

  useEffect(() => {
    if (!wards.length) {
      return;
    }
    wardId.setValue(
      sensor && sensor.inventoryLocation && sensor.inventoryLocation.location
        ? Number(sensor.inventoryLocation.location.id)
        : wardId.value,
    );
  }, [JSON.stringify(wards), sensor]);

  useEffect(() => {
    isView.setValue(originIsView);
  }, [originIsView]);

  const getSchema = () => {
    return {
      code: Yup.string()
        .trim()
        .when('type', {
          is: (type: SensorType) => type === SensorType.SENSOR_WEIGHT,
          then: (schema) =>
            schema
              .required('MAC Address is required')
              // .matches(MAC_ADDRESS_REGEX, 'Invalid MAC address format')
              .min(8, 'Minimum 8 characters')
              .max(30, 'Maximum 30 characters'),
          otherwise: (schema) => schema.notRequired(),
        }),
      piId: Yup.string()
        .trim()
        .nullable()
        .when('type', {
          is: (type: SensorType) =>
            type === SensorType.SENSOR_CV ||
            type === SensorType.SENSOR_CV_EXPIRY ||
            type === SensorType.SENSOR_HANGER,
          then: (schema) => schema.required('Edge ID is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      cameraId: Yup.string()
        .trim()
        .nullable()
        .when('type', {
          is: (type: SensorType) =>
            type === SensorType.SENSOR_CV || type === SensorType.SENSOR_CV_EXPIRY,
          then: (schema) => schema.required('Camera ID is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      viewId: Yup.string()
        .trim()
        .nullable()
        .when('type', {
          is: (type: SensorType) =>
            type === SensorType.SENSOR_CV || type === SensorType.SENSOR_CV_EXPIRY,
          then: (schema) => schema.required('View ID is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      hangerId: Yup.string()
        .trim()
        .nullable()
        .when('type', {
          is: (type: SensorType) => type === SensorType.SENSOR_HANGER,
          then: (schema) => schema.required('Hanger ID is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
    };
  };

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    }
    setItemForUpdate(emptyListViewProvider);
  };

  const formik = useFormik({
    initialValues: formRequest,
    validationSchema: Yup.object().shape(getSchema()),
    onSubmit: async (values, { setSubmitting }) => {
      if (isView.value) {
        isView.setValue(false);
        if (itemForUpdate) {
          setItemForUpdate({
            ...itemForUpdate,
            isView: false,
          });
        }
        return;
      }
      setSubmitting(true);
      const request: SensorRequest = {
        wardId: wardId.value,
        type: values.type.toString(),
        code: !isCV && !isHanger ? values.code : values.code,
        ...(isCV && {
          piId: values.piId,
          cameraId: values.cameraId,
          viewId: values.viewId,
        }),
        ...(isHanger && {
          piId: values.piId,
          hangerId: values.hangerId,
        }),
      };

      if (permission.systemAdmin) {
        request['orgId'] = organization?.id ?? 0;
      }

      try {
        if (sensor) {
          await updateSensor(sensor.id, request);
          toast.success(`Update ${titleNotification} successfully!`);
          cancel(true);
          return;
        }

        await createSensor({
          ...request,
          type: values.type.toString(),
        });
        toast.success(`Add ${titleNotification} successfully!`);
        // if (onShowCodeWhenAddDevice && dataCreateSensor) {
        //   onShowCodeWhenAddDevice(dataCreateSensor);
        // }
        if (Number(state.page) !== 1) {
          cancel();
          onChangeParams({ page: 1 });
          return;
        }
        cancel(true);
      } catch (error: any) {
        getResponseError(error);
      } finally {
        setSubmitting(true);
      }
    },
  });

  const renderLabelButtonSubmit = () => {
    if (sensor) {
      return isView.value ? 'Edit' : 'Save';
    }
    return 'Add';
  };

  const isCV =
    formik.values.type === SensorType.SENSOR_CV ||
    formik.values.type === SensorType.SENSOR_CV_EXPIRY;

  const isHanger = formik.values.type === SensorType.SENSOR_HANGER;

  const handleSelectType = (select: { value: number }) => {
    wardId.setValue(0);
    formik.resetForm();
    formik.setFieldValue('type', select.value);
  };

  useEffect(() => {
    if (isCV) {
      const { piId, cameraId, viewId } = formik.values;
      const codeValue = [piId, cameraId, viewId].filter(Boolean).join('-');
      formik.setFieldValue('code', codeValue);
    } else if (isHanger) {
      const { piId, hangerId } = formik.values;
      if (piId && hangerId) {
        formik.setFieldValue('code', `${piId}-${hangerId}`);
      }
    }
  }, [
    formik.values.piId,
    formik.values.cameraId,
    formik.values.viewId,
    formik.values.hangerId,
    formik.values.type,
  ]);

  return (
    <>
      <form id="kt_modal_add_user_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div
          className="d-flex flex-column scroll-y me-n7 pe-7 ps-1"
          id="kt_modal_add_user_scroll"
          data-kt-scroll="true"
          data-kt-scroll-activate="{default: false, lg: true}"
          data-kt-scroll-max-height="auto"
          data-kt-scroll-dependencies="#kt_modal_add_user_header"
          data-kt-scroll-wrappers="#kt_modal_add_user_scroll"
          data-kt-scroll-offset="300px"
        >
          <FormSelect
            label="Type"
            handleChange={handleSelectType}
            optionsSelect={types.map((el) => {
              return {
                title: el.title,
                value: el.value ?? ' ',
              };
            })}
            activeSelect={formik.values.type}
            isRequired
            fieldActive="value"
            fieldSelect="title"
            styleSelectCard={{
              width: 580,
            }}
            isDisable={isView.value || Boolean(sensor)}
            placementCustom="bottom-start"
            inputPlaceholder="Select type"
          />
          {isCV && (
            <>
              <FormInput
                label="Edge ID"
                placeholder="Enter Edge ID"
                formik={formik}
                field="piId"
                isRequired
              />
              <FormInput
                label="Camera ID"
                placeholder="Enter Camera ID"
                formik={formik}
                field="cameraId"
                isRequired
              />
              <FormInput
                label="View ID"
                placeholder="Enter View ID"
                formik={formik}
                field="viewId"
                isRequired
              />
            </>
          )}
          {isHanger && (
            <>
              <FormInput
                label="Edge ID"
                placeholder="Enter Edge ID"
                formik={formik}
                field="piId"
                isRequired
              />
              <FormInput
                label="Hanger ID"
                placeholder="Enter Hanger ID"
                formik={formik}
                field="hangerId"
                isRequired
              />
            </>
          )}
          <FormInput
            label="Sensor Address"
            placeholder={!isCV && !isHanger ? 'Example: 00:00:00:00:00:00' : ''}
            formik={formik}
            field="code"
            isRequired={!isCV && !isHanger}
            isDisable={isCV || isHanger}
          />
          <FormSelect
            label="Location"
            handleChange={(select) => wardId.setValue(select.value)}
            optionsSelect={wards.map((el) => {
              return {
                title: el.name,
                value: el.id ?? ' ',
              };
            })}
            activeSelect={wardId.value}
            isRequired
            fieldActive="value"
            fieldSelect="title"
            styleSelectCard={{
              width: 580,
            }}
            isDisable={isView.value}
            placementCustom="bottom-start"
            inputPlaceholder="Select location"
          />
        </div>
        <ActionButton
          onCancel={() => cancel()}
          disableButton={formik.isSubmitting || !formik.isValid || !formik.touched || !wardId.value}
          isSubmitting={formik.isSubmitting}
          labelButtonSubmit={renderLabelButtonSubmit()}
        />
      </form>
      {formik.isSubmitting && <LoadingCard />}
    </>
  );
};

export { FormDeviceSensorCard };
